import { isEssentialOnly } from "@/lib/essential-only";
// Consent Mode: production loads gtag with analytics_storage granted for ordinary product analytics (no
// opt-in) and every advertising signal denied — the site runs no ad tracker, and the visitor's advertising
// choice cannot change that. Staging and preview hosts never initialize the production property.
import { consentSignals } from "@/lib/consent";

const GA_ID = "G-YMTRKT89WF"; // GTM_ID_SNOWBUS — the only id that fires on snowbus.com

// Live picks the id by domain; this build gates on the live hostnames so staging/preview hosts never
// post hits into the production property (see Analytics.tsx's longer note on why host, not env var).
const LIVE_HOSTS = new Set(["www.snowbus.com", "snowbus.com"]);

// Google's supported tag-level cross-domain measurement (developers.google.com/tag-platform/devguides/cross-domain):
// SnowBus and Accounts run the same linker config so a visit keeps one session across both hosts.
const LINKER_DOMAINS = ["snowbus.com", "accounts.mysnowclub.com"];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export function isLiveHost(hostname: string): boolean {
  return LIVE_HOSTS.has(hostname);
}

// ── internal traffic (founder 13:48) ──────────────────────────────────────────────────────────────
// `?internal=1` marks this browser as internal for a year (`sg_internal=1`); `?internal=0` clears it (QA). The
// control parameter is then removed from the address bar with replaceState, BEFORE gtag reads page_location, so it
// never reaches an analytics URL (canonicals are path-only already). While the cookie is present every GA4 hit —
// config page_view and every later event — carries `traffic_type: "internal"` for the property's internal-traffic
// filter. It is a marker, not a consent signal: it never changes Consent Mode or the advertising choice.
const INTERNAL_COOKIE = "sg_internal";
const INTERNAL_PARAM = "internal";

export function isInternalTraffic(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split("; ").some((c) => c === `${INTERNAL_COOKIE}=1`);
}

/** Apply and strip a `?internal=` control. Idempotent; runs on every host (the cookie sends nothing). */
export function applyInternalTrafficControl(): "set" | "cleared" | "none" {
  if (typeof window === "undefined" || typeof document === "undefined") return "none";
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return "none";
  }
  const control = url.searchParams.get(INTERNAL_PARAM);
  if (control !== "1" && control !== "0") return "none";
  const secure = url.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    control === "1"
      ? `${INTERNAL_COOKIE}=1; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax${secure}`
      : `${INTERNAL_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${secure}`;
  url.searchParams.delete(INTERNAL_PARAM);
  try {
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  } catch {
    /* no history API: the marker is still applied */
  }
  return control === "1" ? "set" : "cleared";
}

// Module-level, not a ref or state: this must hold across both call sites (the Analytics mount effect
// and ConsentBanner's decide handler), which share no component tree, and there is no un-granting
// within a single page load that would ever need to flip it back.
let initialized = false;

/** Loads GA4 for every measurable production visit: analytics storage granted, advertising signals denied. */
export function initGoogleAnalytics(): void {
  if (typeof window === "undefined") return;
  // Youth 13-15 / an age-step answer under 16 on this page: essential-only, GA4 never loads (@/lib/essential-only).
  if (isEssentialOnly()) return;
  if (initialized) {
    notifyAnalyticsRecheck(); // a re-grant on a page whose tag already exists
    return;
  }
  if (window.dataLayer && window.gtag) {
    initialized = true; // e.g. a hot-reload during dev already set these up
    notifyAnalyticsRecheck();
    return;
  }
  initialized = true;
  applyInternalTrafficControl(); // before any gtag command, so page_location never carries the control

  const dataLayer = (window.dataLayer = window.dataLayer ?? []);
  // Official snippet shape: gtag.js ignores plain arrays in the dataLayer and only processes queued
  // `arguments` objects, so this must push `arguments` itself, not a rest-parameter array.
  window.gtag = function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };

  window.gtag("consent", "default", consentSignals());
  // The linker must be set before js/config so the first config decorates outbound links.
  window.gtag("set", "linker", { domains: LINKER_DOMAINS, accept_incoming: true });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.body.appendChild(s);

  // `set`, not a config field: it applies to the config page_view AND every later event, including SPA page views.
  if (isInternalTraffic()) window.gtag("set", { traffic_type: "internal" });

  window.gtag("js", new Date());
  // No manual route-change page_view: live does not send one either — see Analytics.tsx's note.
  window.gtag("config", GA_ID);
  notifyAnalyticsRecheck();
}

// Every grant path ends here, so this is the one signal that the GA4 gates may just have opened. A page that held an
// event back (a search handoff written before the tag existed, a result list shown before consent) listens and retries.
const RECHECK_EVENT = "sg:analytics-recheck";

function notifyAnalyticsRecheck(): void {
  try {
    window.dispatchEvent(new Event(RECHECK_EVENT));
  } catch {
    /* no event target in this environment: nothing is waiting */
  }
}

/** Run `fn` each time analytics may have become sendable. Returns the unsubscribe. */
export function onAnalyticsRecheck(fn: () => void): () => void {
  if (typeof window === "undefined" || typeof window.addEventListener !== "function") return () => undefined;
  window.addEventListener(RECHECK_EVENT, fn);
  return () => window.removeEventListener(RECHECK_EVENT, fn);
}

/** Test-only: initGoogleAnalytics's "exactly once" guard is module-level state, so a test suite that
 *  exercises multiple transitions needs a way back to a clean slate between them. */
export function _resetAnalyticsForTests(): void {
  initialized = false;
  if (typeof window !== "undefined") {
    delete (window as { dataLayer?: unknown[] }).dataLayer;
    delete (window as { gtag?: unknown }).gtag;
  }
}
